import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import * as bomAction from '../Bom/store/BOMConfig.action';
import * as action from '../store/DevelopmentOrder.action';
import Annotation from '../../../Components/Utility/Annotation/DesignAnnotation';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import Loader from '../../../Components/Utility/Loader/Loader';

function DesignDetail({ match }) {
  const { t } = useTranslation('common');
  const reducer = useSelector((reducerState) => reducerState);
  const { Measuremets } = reducer;
  const { SingleDesignDetailData } = Measuremets;

  /* useState */
  const [obj1Confirm, setObj1Confirm] = useState({});
  const [updateURLConfirm, setUpdateURLConfirm] = useState('');
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [count, setCount] = useState(-1);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [loader, setLoader] = useState(true);
  const [imageEditReplace, setImageEditReplace] = useState('');
  const [isInitialImageLoaded, setIsInitialImageLoaded] = useState(false);
  const [currentImage, setCurrentImage] = useState({
    annotedImage: '',
    name: '',
    createdAt: '',
    createdBy: '',
    image: '',
    isNewItem: null,
    isVisibleToSupplier: null,
    modifiedAt: '',
    modifiedBy: '',
    markerObj: {},
    _id: '',
    isArchived: false,
    isDeleted: false,
    isDefaultImg: false,
  });

  const { params: matchParams } = match;
  const { devOrderId: devOrderID } = matchParams;
  const dispatch = useDispatch();

  const { openType, designId, imageId } = useMemo(() => {
    const { search } = window.location;
    const urlParams = new URLSearchParams(search);
    return {
      openType: urlParams.get('type'),
      designId: urlParams.get('designId'),
      imageId: urlParams.get('imageId'),
    };
  }, []);

  const initialExecution = useCallback(async () => {
    await dispatch(bomAction.fetchTrafficLights(devOrderID));
    if (designId) {
      await dispatch(action.getSingleDesignDetail(devOrderID, designId));
    } else {
      await dispatch(action.getDesignDetail(devOrderID));
    }
    setLoader(false);
  }, [dispatch, devOrderID, designId]);

  useEffect(() => {
    initialExecution();
  }, [initialExecution]);

  const foundImageData = useMemo(() => {
    if (!SingleDesignDetailData?.images?.length || !imageId) {
      return null;
    }

    const foundImage = SingleDesignDetailData.images.find((img) => img?._id === imageId);
    if (!foundImage) {
      return null;
    }

    const foundImageIndex = SingleDesignDetailData.images.findIndex((img) => img?._id === imageId);
    return {
      image: foundImage,
      design: SingleDesignDetailData,
      imageIndex: foundImageIndex,
    };
  }, [SingleDesignDetailData, imageId]);

  useEffect(() => {
    if (!foundImageData || isInitialImageLoaded) {
      return;
    }

    const { image: foundImage, design, imageIndex } = foundImageData;
    setCurrentImage({
      annotedImage: foundImage?.annotedImage ?? foundImage?.image,
      markerObj: foundImage?.markerObj ?? {},
      name: design?.name,
      createdAt: design?.createdAt,
      createdBy: design?.createdBy,
      image: foundImage?.image || foundImage?.s3Key,
      isNewItem: foundImage?.isNewItem,
      isVisibleToSupplier: design?.isVisibleToSupplier,
      modifiedAt: design?.modifiedAt,
      modifiedBy: design?.modifiedBy,
      _id: design?._id,
      isArchived: design?.isArchived,
      isDeleted: design?.isDeleted,
      isDefaultImg: foundImage?.isDefaultImg ?? false,
      designId: design?._id,
      imageId: foundImage?._id,
    });
    setCount(imageIndex);

    if (openType) {
      setImageEditReplace(openType);
    }

    setIsInitialImageLoaded(true);
  }, [foundImageData, openType, isInitialImageLoaded]);

  useEffect(() => {
    if (!SingleDesignDetailData?.images?.length || count < 0 || count >= SingleDesignDetailData.images.length) {
      return;
    }

    const currentImg = SingleDesignDetailData.images[count];
    if (!currentImg) {
      return;
    }

    setCurrentImage({
      annotedImage: currentImg?.annotedImage ?? currentImg?.image,
      markerObj: currentImg?.markerObj ?? {},
      name: SingleDesignDetailData?.name,
      createdAt: SingleDesignDetailData?.createdAt,
      createdBy: SingleDesignDetailData?.createdBy,
      image: currentImg?.image || currentImg?.s3Key,
      isNewItem: currentImg?.isNewItem,
      isVisibleToSupplier: SingleDesignDetailData?.isVisibleToSupplier,
      modifiedAt: SingleDesignDetailData?.modifiedAt,
      modifiedBy: SingleDesignDetailData?.modifiedBy,
      _id: SingleDesignDetailData?._id,
      isArchived: SingleDesignDetailData?.isArchived,
      isDeleted: SingleDesignDetailData?.isDeleted,
      isDefaultImg: currentImg?.isDefaultImg ?? false,
      designId: SingleDesignDetailData?._id,
      imageId: currentImg?._id,
    });
  }, [count, SingleDesignDetailData]);

  const onEditModeOn = useCallback((val) => {
    setIsEditOpen(val);
  }, []);

  const saveAnnotation = useCallback(
    async (obj1, obj2, updateURL) => {
      const obj1Markers = obj1?.markers ?? [];
      const obj2Markers = obj2?.markers ?? [];
      const markersChanged = JSON.stringify(obj1Markers) !== JSON.stringify(obj2Markers);

      if (markersChanged) {
        setLoader(true);

        const postParams = {
          annotedImage: updateURL,
          markerObj: obj1,
        };

        try {
          const result = await dispatch(
            action.saveDesignAnnotation(devOrderID, currentImage?.designId || designId, currentImage?.imageId || imageId, postParams, t),
          );
          if (result) {
            if (designId) {
              await dispatch(action.getSingleDesignDetail(devOrderID, designId));
            } else {
              await dispatch(action.getDesignDetail(devOrderID));
            }
          }
        } finally {
          setLoader(false);
          setObj1Confirm({});
          setUpdateURLConfirm('');
          setConfirmLoader(false);
        }
      }
    },
    [dispatch, devOrderID, currentImage?.designId, currentImage?.imageId, designId, imageId, t],
  );

  const onSave = useCallback(
    (obj, newObj, updatedUrl) => {
      saveAnnotation(obj, newObj, updatedUrl);
    },
    [saveAnnotation],
  );

  const totalCount = useMemo(() => {
    if (!SingleDesignDetailData?.images?.length) {
      return 0;
    }
    const imagesWithImageKey = SingleDesignDetailData.images.filter((img) => img?.image);
    if (!imagesWithImageKey.length) {
      return 0;
    }
    return imagesWithImageKey.length - 1;
  }, [SingleDesignDetailData?.images]);

  const handleNext = useCallback(() => {
    setCount((prev) => prev + 1);
    setImageEditReplace('edit');
  }, []);

  const handlePrevious = useCallback(() => {
    setCount((prev) => prev - 1);
    setImageEditReplace('edit');
  }, []);

  const sampleImgSet = useMemo(() => {
    return currentImage?.annotedImage || currentImage?.image;
  }, [currentImage?.annotedImage, currentImage?.image]);

  const annotationComponent = useMemo(() => {
    if (!currentImage?.image) {
      return <div className="text-center pt-50per">{t('TAKING_DATES.NO_IMAGE_FOUND')}</div>;
    }

    return (
      <Annotation
        key={currentImage?.imageId || currentImage?.image}
        sampleImage={sampleImgSet}
        sourceImage={currentImage?.image}
        markerObj={currentImage?.markerObj}
        onSaveAnnotation={onSave}
        setIsEditOpen={onEditModeOn}
        openType={imageEditReplace}
      />
    );
  }, [sampleImgSet, currentImage?.image, currentImage?.markerObj, currentImage?.imageId, onSave, onEditModeOn, imageEditReplace, t]);

  useEffect(() => {
    const callFn = async () => {
      await dispatch(bomAction.fetchProductDetails(devOrderID));
    };
    if (!isEmpty(devOrderID)) {
      callFn();
    }
  }, [dispatch, devOrderID]);

  const closeConfirmationsPopup = useCallback(() => {
    setShowConfirmationPopup(false);
    setConfirmLoader(false);
    setObj1Confirm({});
    setUpdateURLConfirm('');
  }, []);

  const confirmRemoveOldData = useCallback(() => {
    setConfirmLoader(true);
    saveAnnotation(obj1Confirm, {}, updateURLConfirm);
    setShowConfirmationPopup(false);
  }, [obj1Confirm, updateURLConfirm, saveAnnotation]);

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('TAKING_DATES.DESIGN_DETAIL')}</h4>
          {currentImage?.name !== '' && (
            <div className="reference-title">
              <span className="title edit-product-icon"> | {currentImage?.name}</span>
            </div>
          )}
        </div>
        <div>
          <Link className="cursor-pointer text-decoration-none p-0 border-0" to={`/development-order/${devOrderID}/design-details/list`}>
            <i className="icon-close f-20" />
          </Link>
        </div>
      </div>
      {loader ? (
        <Loader />
      ) : (
        <div className="container-fluid">
          <div className="row justify-content-center pt-3" style={{ marginBottom: '50px' }}>
            <div className="col-lg-11">
              {annotationComponent}
              <button
                aria-label="btn"
                type="button"
                className="btn-marker-nav next"
                onClick={handleNext}
                disabled={count >= totalCount || isEditOpen || !SingleDesignDetailData?.images?.length}
              >
                <span className="iconify" data-icon="grommet-icons:next" />
              </button>
              <button
                aria-label="btn"
                type="button"
                className="btn-marker-nav prev"
                onClick={handlePrevious}
                disabled={count <= 0 || isEditOpen || !SingleDesignDetailData?.images?.length}
              >
                <span className="iconify" data-icon="grommet-icons:previous" />
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup}
          onConfirm={confirmRemoveOldData}
          title={t('TAKING_DATES.CONFORM_ACTION')}
          message={t('TAKING_DATES.CONFORM_MESSAGE')}
          toggle={closeConfirmationsPopup}
          loading={confirmLoader}
        />
      )}
    </>
  );
}

DesignDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      devOrderId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default DesignDetail;
